import {
  CustomViewAttribute,
  CustomViewSortConfigItem,
  CustomViewSorting,
} from 'src/graphql/entities/custom-view/custom-view.types';
import { SortingInput } from 'src/graphql/types';

export const getDefaultAccessors = (path: string) => {
  const gqlLikePath = path.replace(/\./g, '/');

  return {
    sort: gqlLikePath,
    filter: gqlLikePath,
    display: path,
  };
};

export const prepareSortAttributes = <T extends string>(
  sort?: CustomViewSorting,
  accessors?: Record<T, CustomViewSortConfigItem>,
): SortingInput[] | SortingInput | undefined => {
  if (!sort?.accessor) {
    return;
  }

  let aggFunction: SortingInput['aggFunction'];
  if (sort.aggFunction) {
    aggFunction = sort.aggFunction;
  } else if (accessors) {
    const accessorKey = sort.attribute as T;
    aggFunction = accessors?.[accessorKey]?.aggFunction;
  }

  // custom attribute columns carry the definition default so the backend coalesces an unset value to
  // it (sort order then matches the cell). built-in sorts have no default, so it is omitted.
  const coalesceDefault = sort.coalesceDefault
    ? { coalesceDefault: sort.coalesceDefault }
    : {};

  const sortingAttributes = Array.isArray(sort?.accessor)
    ? sort?.accessor.map((attribute: string) => ({
        attribute,
        desc: sort.desc,
        ...(aggFunction ? { aggFunction } : {}),
        ...coalesceDefault,
      }))
    : [
        {
          attribute: sort.accessor,
          desc: sort.desc,
          ...(aggFunction ? { aggFunction } : {}),
          ...coalesceDefault,
        },
      ];

  // append sorting by 'createdAt' to ensure deterministic ordering of items
  // when multiple items share the same values. using createdAt instead of id
  // because sorting by uuid columns is not allowed by our query builder
  return [...sortingAttributes, { attribute: 'createdAt' }];
};

export const mapSortAttributeToFormInterface = (
  attribute: CustomViewAttribute,
  overrides?: Partial<CustomViewSorting>,
): CustomViewSorting => {
  return {
    label: attribute.attributeLabel,
    accessor: attribute.sortingKey,
    attribute: attribute.attribute,
    id: attribute.attribute,
    value: attribute.attribute,
    desc: true,
    aggFunction: attribute.sortingAggFunction,
    coalesceDefault: attribute.sortingCoalesceDefault,
    ...overrides,
  };
};
